import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, getToken } from '../api/client';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { ZoomImage } from '../components/ZoomImage';
import { useGameCountdown } from '../hooks/useGameCountdown';
import { useAuth } from '../hooks/useAuth';
import { preloadImages } from '../utils/imageCache';

import type { PhotoAttributionData } from '../components/PhotoAttribution';

interface TodayResponse {
  puzzle: { id: string; puzzleNumber: number; attribution?: PhotoAttributionData | null };
  imageUrl: string;
  zoomLevels: { level: number; imageUrl: string }[];
  currentZoomLevel: number;
  run: {
    id: string;
    status: string;
    dezoomsUsed?: number;
    currentZoomLevel?: number;
    startedAt?: string;
  } | null;
  hasOfficialRun: boolean;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
  shareText: string | null;
  durationSec: number | null;
  puzzleId?: string;
  answer?: string;
  answerEn?: string;
  imageUrl?: string;
}

export function PlayPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, guest, loginGuest, loading: authLoading } = useAuth();
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [zoomLevels, setZoomLevels] = useState<{ level: number; imageUrl: string }[]>([]);
  const [dezooms, setDezooms] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timerSec, setTimerSec] = useState(0);
  const [skipCountdown, setSkipCountdown] = useState(false);
  const [initDone, setInitDone] = useState(false);

  const startRun = useCallback(async () => {
    if (runId) return;
    const res = await api<{ runId: string; startedAt: string }>('/runs/start', { method: 'POST' });
    setRunId(res.runId);
    const t = new Date(res.startedAt).getTime();
    setStartedAt(t);
    setTimerSec(0);
  }, [runId]);

  const { display, revealed, ready } = useGameCountdown({
    enabled: initDone && !skipCountdown,
    skip: initDone && skipCountdown,
    onGo: startRun,
  });

  const init = useCallback(async () => {
    if (params.get('guest') === '1' && !user && !guest && !getToken()) {
      await loginGuest();
    }
    const data = await api<TodayResponse>('/puzzles/today');
    const levels = data.zoomLevels || [];
    const level = data.run?.currentZoomLevel ?? data.currentZoomLevel;
    setToday(data);
    setZoomLevels(levels);
    setZoomLevel(level);
    preloadImages(levels.map((z) => z.imageUrl));

    if (data.run) {
      setDezooms(data.run.dezoomsUsed ?? 0);
      if (data.run.status !== 'in_progress') {
        navigate('/result', { state: { fromRun: data.run.id } });
        return;
      }
      setRunId(data.run.id);
      if (data.run.startedAt) {
        setStartedAt(new Date(data.run.startedAt).getTime());
      }
      setSkipCountdown(true);
    } else {
      setSkipCountdown(false);
    }
    setInitDone(true);
  }, [params, user, guest, loginGuest, navigate]);

  useEffect(() => {
    if (authLoading) return;
    init().catch((e) => setError(e.message));
  }, [authLoading, init]);

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => {
      setTimerSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    if (!runId) return;
    setBusy(true);
    setError('');
    try {
      const res = await api<GuessResponse>(`/runs/${runId}/guess`, {
        method: 'POST',
        body: JSON.stringify({ guess }),
      });

      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);

      if (res.status !== 'in_progress') {
        navigate('/result', {
          state: {
            won: res.status === 'won',
            score: res.score ?? 0,
            dezoomsUsed: res.dezoomsUsed,
            durationSec: res.durationSec ?? 0,
            shareText: res.shareText,
            puzzleNumber: today?.puzzle.puzzleNumber ?? 0,
            puzzleId: today?.puzzle.id,
            answer: res.answer,
            answerEn: res.answerEn,
            imageUrl: res.imageUrl,
          },
        });
        return;
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || 'Erreur');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!today) {
    return <main className="page play"><p className="loading">{error || 'Chargement…'}</p></main>;
  }

  return (
    <main className="page play">
      <header className="play-header">
        <div className="play-header-left">
          <span className="puzzle-num">#{today.puzzle.puzzleNumber}</span>
          {startedAt != null && (
            <span className="play-timer" aria-live="polite">{formatTimer(timerSec)}</span>
          )}
        </div>
        <DezoomDots used={dezooms} />
      </header>

      <div className={`zoom-stage${revealed ? ' is-revealed' : ''}`}>
        <ZoomImage levels={zoomLevels} activeLevel={zoomLevel} attribution={today.puzzle.attribution} />
        <CountdownOverlay value={display} />
      </div>

      <footer className="play-footer">
        {error && <p className="play-error" role="alert">{error}</p>}
        <GuessForm onSubmit={handleGuess} disabled={busy || !ready || !runId} />
      </footer>
    </main>
  );
}
